import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subject, Subscription, combineLatest, distinctUntilChanged, filter } from 'rxjs';
import { CalendarOptions, DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import allLocales from '@fullcalendar/core/locales-all';
import { FullCalendarComponent } from '@fullcalendar/angular/full-calendar.component';
import { CalendarException } from 'src/app/model/calendar-exception';
import * as moment from 'moment';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PERMISSIONS_CALENDAR_HEADER_TOOLBAR } from 'src/app/conf/permissions';
import { Permission } from 'src/app/model/permission';
import * as PermissionActions from 'src/app/store/permissions/permissions.actions';
import { TranslateService } from '@ngx-translate/core';
import { selectPermissionsTabIndex } from 'src/app/store/home/home.selector';
import { DEFAULT_LANG } from 'src/app/conf/languages';
import { Lang } from 'src/app/model/lang';
import { selectCurrentLang } from 'src/app/store/user/user.selector';
import { selectExceptions } from 'src/app/store/calendar/calendar.selector';
import { selectPermissions, selectPermissionsCalendar } from 'src/app/store/permissions/permissions.selector';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { Site } from 'src/app/model/site';
import { PermissionInfoDialogComponent } from '../permission-info-dialog/permission-info.dialog.component';

@Component({
	selector: 'app-permissions-calendar',
	templateUrl: './permissions-calendar.component.html',
	providers: [DialogService],
})

export class PermissionsCalendarComponent implements OnInit, OnDestroy {
	@ViewChild('permissionCalendar') calendarComponent: FullCalendarComponent;

	tabIndex: number;
	tabIndex$: Observable<number | undefined>;
	tabIndexSub: Subscription | undefined;

	evntSub: Subscription;

	permissions: Permission[];
	permissions$: Observable<Permission[] | undefined>;
	permissionsTableSub: Subscription | undefined;

	permissionsCalendar$: Observable<Permission[] | undefined >;
	
	exceptions: CalendarException[];
	exceptions$: Observable<CalendarException[] | undefined >;

	exceptionsAsString: string[];
	dateRangeSwitch$ = new Subject<DatesSetArg>();
	dateInfo: DatesSetArg;

	site$: Observable < Site | undefined >;
	siteSub: Subscription;

	dateView: DatesSetArg;

	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;

	langSub: Subscription;
	calendarLang = 'it';

	calendarOptions: CalendarOptions = {
		locales: allLocales,
		locale: this.calendarLang,
		contentHeight: 600,
		initialView: 'dayGridMonth',
		headerToolbar: PERMISSIONS_CALENDAR_HEADER_TOOLBAR,
		eventTimeFormat: { hour: '2-digit', minute: '2-digit', meridiem: false },
		plugins: [dayGridPlugin,timeGridPlugin],
		datesSet: (dateInfo: DatesSetArg) => {
			this.dateInfo = dateInfo;
			this.dateRangeSwitch$.next(dateInfo);
		},
		eventClick: (info: EventClickArg) => {
			const permission = this.permissions.find(s => s.Id === info.event.extendedProps['permissionId']);
			this.showInfo(permission);
		}
	};

	ref: DynamicDialogRef | undefined;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.dateView = this.dateInfo;

		this.subscribeAll();
	}

	ngOnDestroy(): void {
		this.tabIndexSub?.unsubscribe();
		this.evntSub?.unsubscribe();
		this.permissionsTableSub?.unsubscribe();
		this.langSub?.unsubscribe();
		this.siteSub?.unsubscribe();
	}

	initSelectors(): void {
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.tabIndex$ = this.store.select(selectPermissionsTabIndex);
		this.site$ = this.store.select(selectCurrentSite);
		this.exceptions$ = this.store.select(selectExceptions);
		this.permissions$ = this.store.select(selectPermissions);
		this.permissionsCalendar$ = this.store.select(selectPermissionsCalendar);
	}

	subscribeAll(): void {
		this.tabIndexSub = this.tabIndex$?.subscribe(
			async (tabIndex: number) => {
				this.tabIndex = tabIndex;
				if (tabIndex === 1) {
					this.dateRangeSwitch$.next(this.dateInfo);
					if (this.calendarComponent !== undefined) {
						setTimeout(() =>
							{
								const calendarApi = this.calendarComponent.getApi();
								calendarApi.render();
							},
						5);
					}
				}
			}
		);

		this.langSub = this.currentLang$.subscribe(lang => {
			this.currentLang = lang;
			this.calendarLang = lang.code.slice(0, 2);
			this.dateRangeSwitch$.next(this.dateInfo);
		});

		this.evntSub = combineLatest([
			this.permissionsCalendar$,
			this.exceptions$,
			this.tabIndex$,
			this.dateRangeSwitch$,
		]).pipe(
			distinctUntilChanged(),
		).subscribe(([permissions, exceptions, tabIndex, dateRange]) => {
			if (tabIndex !== 1) {
				return;
			}
			this.permissions = permissions;
			this.exceptions = exceptions;
			this.rangeSchedules(dateRange);
			if (this.dateView !== this.dateInfo){
				this.dateView=this.dateInfo;
				this.store.dispatch(PermissionActions.getPermissionsCalendar({ datefrom: this.dateInfo.start.toISOString(), dateto: this.dateInfo.end.toISOString() }));
			}
		});

		this.siteSub = this.site$.pipe(filter(s => !!s)).subscribe(() => {
			if (this.dateInfo){
				this.store.dispatch(PermissionActions.getPermissionsCalendar({ datefrom: this.dateInfo.start.toISOString(), dateto: this.dateInfo.end.toISOString() }));
			}
			
		});

		this.permissionsTableSub = this.permissions$.pipe(filter(s => !!s)).subscribe(() => {
			if (this.dateInfo){
				this.store.dispatch(PermissionActions.getPermissionsCalendar({ datefrom: this.dateInfo.start.toISOString(), dateto: this.dateInfo.end.toISOString() }));
			}
		});
	}

	rangeSchedules(dateRange: DatesSetArg): void {
		const calendarApi = this.calendarComponent.getApi();
		calendarApi.setOption('locale', this.calendarLang);
		calendarApi.removeAllEvents();
		for (const permission of this.permissions) {
			if (this.dateRangeOutOfScope(permission, dateRange)) {
				continue;
			}
			this.buildEvents(permission, dateRange);
		}
		calendarApi.render();
	}

	buildEvents(permission: Permission, dateRange: DatesSetArg): void {
		const calendarApi = this.calendarComponent.getApi();
		const event = this.getEventsFromPermission(permission);
		calendarApi.addEvent(event, true);
	}

	getEventsFromPermission(permission: Permission): EventInput {
		let calendarText = permission.Name;
		if (permission.Description !== null && permission.Description !== '') {
			calendarText = calendarText + ' - '+ permission.Description;
		}
		const event: EventInput = {
			title: calendarText,
			start: new Date(permission.Start),
			end: new Date(new Date(permission.End)),
			color: this.getBackgroudColorPermission(permission),
			textColor: this.getTextColorPermission(permission),
			classNames: permission.Status + '-event',
			extendedProps: {
				permissionId: permission.Id,
			}
		};
		return event;
	}

	getBackgroudColorPermission(permission: Permission): string{
		switch (permission.Status) {
			case 'expired':
				return '#f6b3a2';	// $redNormal, $lightness: +50%
			case 'active':
				return '#a9d6bd'; // $greenNormal, $lightness: +50%
			case 'ready':
				return '#fff2d7'; // $yellowLight, $lightness: +50%
			case 'created':
				return '#CCEFFB'; // $lightBlue0220
			default:
				return '#CED1D4'; // $darkBlue20
		}
	}

	getTextColorPermission(permission: Permission): string{
		switch (permission.Status) {
			case 'expired':
				return '#D50000'; // redDark
			case 'active':
				return '#3D7F5A'; // greenDark
			case 'ready':
				return '#906309'; // yellowDark
			case 'created':
				return '#00B0ED'; // primaryColor
			default:
				return '#B6B9BF'; // darkBlue01
		}
	}

	dateInExceptions(date: Date, excDates: string[]): boolean {
		const formattedDate = this.formatDate(date);
		return excDates.includes(formattedDate);
	}

	dateAfterRangeStart(date: moment.Moment, rangeStart: moment.Moment): boolean {
		return date.isSameOrAfter(rangeStart);
	}

	dateBeforeRangeEnd(date: moment.Moment, rangeEnd: moment.Moment): boolean {
		return date.isSameOrBefore(rangeEnd);
	}

	dateAfterScheduleStart(date: moment.Moment, scheduleStart: moment.Moment): boolean {
		return date.isSameOrAfter(scheduleStart);
	}

	dateBeforeScheduleEnd(date: moment.Moment, scheduleEnd?: string): boolean {
		if (!scheduleEnd) {
			return true;
		}
		const se = moment(scheduleEnd);
		return !se || date.isSameOrBefore(se);
	}

	dateRangeOutOfScope(permission: Permission, dateRange: DatesSetArg): boolean {
		return (permission.End && new Date(permission.End) < dateRange.start) || new Date(permission.Start) > dateRange.end;
	}

	getExclusionDates(calendarId: number): string[] {
		const exceptions = this.getCaledarExceptions(this.exceptions, calendarId);
		if (exceptions.length > 0) {
			return exceptions.map(e => e.Date.slice(0, 10));
		}
		return [];
	}

	getCaledarExceptions(exceptions: CalendarException[], calendarId?: number): CalendarException[] {
		let exc = [];
		if (calendarId) {
			exc = exceptions.filter(e => e.CalendarId === calendarId);
		}
		return exc;
	}

	formatDate(d: Date): string {
		let month = '' + (d.getMonth() + 1);
		let day = '' + d.getDate();
		const year = d.getFullYear();

		if (month.length < 2) {
			month = '0' + month;
		}
		if (day.length < 2) {
			day = '0' + day;
		}
		return [year, month, day].join('-');
	}

	// DIALOG per info EVENTO
	showInfo(permission: Permission): void {
		this.store.dispatch(PermissionActions.resetPermissionForm());
		this.ref = this.dialogService.open(PermissionInfoDialogComponent, {
			header: this.translate.instant('PERMISSION') + ': ' + permission.Name,
			width: '50vw',
			breakpoints: { '950px' : '98vw' },
			contentStyle: { overflow: 'auto' },
			data: { permission: permission }
		});
	}

}
